import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  addTechnicalSkill, 
  updateTechnicalSkill, 
  deleteTechnicalSkill,
  addSoftSkill,
  updateSoftSkill,
  deleteSoftSkill 
} from '../../../store/slices/skillsSlice';
import Icon from '../../../components/AppIcon';
import SkillForm from './SkillForm';

const SkillsAdmin = () => {
  const dispatch = useDispatch();
  const { technicalSkills, softSkills } = useSelector(state => state.skills);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [skillType, setSkillType] = useState('technical');

  const handleAddSkill = (skillData) => {
    if (skillType === 'technical') {
      dispatch(addTechnicalSkill(skillData));
    } else {
      dispatch(addSoftSkill(skillData));
    }
    setIsFormOpen(false);
  };

  const handleUpdateSkill = (skillData) => {
    if (skillType === 'technical') {
      dispatch(updateTechnicalSkill(skillData));
    } else {
      dispatch(updateSoftSkill(skillData));
    }
    setEditingSkill(null);
    setIsFormOpen(false);
  };

  const handleDeleteSkill = (skillId, type) => {
    if (window.confirm('Are you sure you want to delete this skill?')) {
      if (type === 'technical') {
        dispatch(deleteTechnicalSkill(skillId));
      } else {
        dispatch(deleteSoftSkill(skillId));
      }
    }
  };

  const handleEditSkill = (skill, type) => {
    setEditingSkill(skill);
    setSkillType(type);
    setIsFormOpen(true);
  };

  const handleOpenForm = (type) => {
    setSkillType(type);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingSkill(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Skills Management</h2>
      </div>

      {/* Technical Skills Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">Technical Skills</h3>
          <button
            onClick={() => handleOpenForm('technical')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90 transition-colors font-medium flex items-center"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Technical Skill
          </button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {technicalSkills.map((skill) => (
            <div key={skill.id} className="bg-surface rounded-lg p-4 security-shadow">
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold text-foreground">{skill.category}</h4>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleEditSkill(skill, 'technical')}
                    className="p-1 text-text-secondary hover:text-primary transition-colors"
                    title="Edit"
                  >
                    <Icon name="Edit" size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteSkill(skill.id, 'technical')}
                    className="p-1 text-text-secondary hover:text-destructive transition-colors"
                    title="Delete"
                  >
                    <Icon name="Trash" size={14} />
                  </button>
                </div>
              </div>
              
              <div className="space-y-1">
                {skill.skills.map((item, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <Icon name="Check" size={12} className="text-accent mr-2 flex-shrink-0" />
                    <span className="text-text-secondary">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Soft Skills Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-foreground">Soft Skills</h3>
          <button
            onClick={() => handleOpenForm('soft')}
            className="bg-secondary text-secondary-foreground px-4 py-2 rounded-md hover:bg-secondary/90 transition-colors font-medium flex items-center"
          >
            <Icon name="Plus" size={16} className="mr-2" />
            Add Soft Skill
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {softSkills.map((skill) => (
            <div key={skill.id} className="bg-surface rounded-lg p-4 security-shadow">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold text-foreground">{skill.skill}</h4>
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleEditSkill(skill, 'soft')}
                    className="p-1 text-text-secondary hover:text-primary transition-colors"
                    title="Edit"
                  >
                    <Icon name="Edit" size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteSkill(skill.id, 'soft')}
                    className="p-1 text-text-secondary hover:text-destructive transition-colors"
                    title="Delete"
                  >
                    <Icon name="Trash" size={14} />
                  </button>
                </div>
              </div>
              
              <p className="text-sm text-text-secondary">{skill.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {isFormOpen && (
        <SkillForm
          skill={editingSkill}
          skillType={skillType}
          onSave={editingSkill ? handleUpdateSkill : handleAddSkill}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default SkillsAdmin; 